/**
 * P2 domain Automations Library Soft≠ invent — lifecycle via /automations/domain/*.
 * Tenant CMC admin desk. Legacy /automations list remains compatibility surface.
 * Hold / Trigger / Cancel are domain HTTP — never remount /automations CRUD.
 */
import { useMemo, useState } from "react";
import { html, Card, Field, Input, Alert, KV } from "./ui.js";
import { path, useAction, useApi } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";

function safeDomainPath(op, opts) {
  try {
    return path(op, opts);
  } catch {
    /* contract lag fallback — Soft≠ invent */
  }
  const id = (opts && opts.pathParams && opts.pathParams[0]) || "";
  if (op === "get__automations_domain_executors") return "/automations/domain/executors";
  if (op === "get__automations_domain_library") return "/automations/domain/library";
  if (op === "post__automations_domain_define") return "/automations/domain/define";
  if (op === "post__automations_domain_p_publish") {
    return `/automations/domain/${encodeURIComponent(id)}/publish`;
  }
  if (op === "post__automations_domain_p_simulate") {
    return `/automations/domain/${encodeURIComponent(id)}/simulate`;
  }
  if (op === "post__automations_domain_p_activate") {
    return `/automations/domain/${encodeURIComponent(id)}/activate`;
  }
  if (op === "post__automations_domain_p_trigger") {
    return `/automations/domain/${encodeURIComponent(id)}/trigger`;
  }
  if (op === "post__automations_domain_activations_p_hold") {
    return `/automations/domain/activations/${encodeURIComponent(id)}/hold`;
  }
  if (op === "post__automations_domain_activations_p_release") {
    return `/automations/domain/activations/${encodeURIComponent(id)}/release`;
  }
  if (op === "post__automations_domain_runs_p_cancel") {
    return `/automations/domain/runs/${encodeURIComponent(id)}/cancel`;
  }
  return "";
}

function neverEnabledPaint(it) {
  const st = String((it && (it.activation_status || it.status)) || "draft");
  if (it && it.painted_enabled === true && st === "ENABLED") return true;
  return false;
}

export function AutomationsDomainPanel({ toast, engineArmed, createAllowed, mutateAllowed, triggerAllowed }) {
  const defineOk = createAllowed === true;
  const mutateOk = mutateAllowed === true;
  const triggerOk = triggerAllowed === true;
  const { run, busy, envelope } = useAction();
  const lib = useApi(safeDomainPath("get__automations_domain_library"));
  const exec = useApi(safeDomainPath("get__automations_domain_executors"));
  const items = useMemo(() => {
    const rows = (lib.data && (lib.data.items || lib.data.automations)) || [];
    return Array.isArray(rows) ? rows : [];
  }, [lib.data]);
  const executors = useMemo(() => {
    const rows = (exec.data && (exec.data.items || exec.data.executors)) || [];
    return Array.isArray(rows) ? rows : [];
  }, [exec.data]);

  const [name, setName] = useState("");
  const [pick, setPick] = useState("");
  const [executorId, setExecutorId] = useState("");
  const [lastSim, setLastSim] = useState(null);
  const [lastRunId, setLastRunId] = useState("");

  const selected = useMemo(
    () => items.find((it) => String(it.automation_id || it.id || "") === pick) || null,
    [items, pick],
  );
  const actId = String((selected && selected.activation_id) || "");
  const actSt = String((selected && (selected.activation_status || "draft")) || "draft");
  const liveEligible = !!(selected && selected.live_eligible && neverEnabledPaint(selected));
  const defaultExecutor = useMemo(() => {
    if (executorId) return executorId;
    const ids = executors.map((e) => e.executor_id || e.id).filter(Boolean);
    if (ids.includes("needs_you_internal_notify")) return "needs_you_internal_notify";
    return ids[0] || "";
  }, [executorId, executors]);

  const defineLib = async () => {
    if (!defineOk) {
      toast && toast("Define sealed", "warn");
      return;
    }
    if (!String(name || "").trim()) {
      toast && toast("Name required", "warn");
      return;
    }
    const ex = String(defaultExecutor || "").trim();
    if (!ex) {
      toast && toast("Pick a registered executor", "warn");
      return;
    }
    try {
      const out = await run(safeDomainPath("post__automations_domain_define"), {
        method: "POST",
        body: {
          name: String(name).trim(),
          scope: "reusable",
          library_visible: true,
          steps: [{ executor: ex, capability: ex, args: {} }],
        },
      });
      if (!out || out.ok === false) {
        toast && toast("Define refused — unknown executor is not a Live step", "warn");
        return;
      }
      toast && toast("Domain automation defined", "go");
      setName("");
      lib.reload && lib.reload();
    } catch {
      toast && toast("Define failed", "bad");
    }
  };

  const publish = async () => {
    if (!mutateOk) {
      toast && toast("Publish sealed", "warn");
      return;
    }
    if (!pick) return toast && toast("Select a domain automation", "warn");
    try {
      const out = await run(
        safeDomainPath("post__automations_domain_p_publish", { pathParams: [pick] }),
        { method: "POST", body: {} },
      );
      if (!out || out.ok === false) {
        toast && toast("Publish refused", "warn");
        return;
      }
      toast && toast("Revision published", "go");
      lib.reload && lib.reload();
    } catch {
      toast && toast("Publish failed", "bad");
    }
  };

  const simulate = async () => {
    if (!pick) return toast && toast("Select a domain automation", "warn");
    try {
      const out = await run(
        safeDomainPath("post__automations_domain_p_simulate", { pathParams: [pick] }),
        { method: "POST", body: {} },
      );
      setLastSim(out || null);
      if (!out || out.ok === false) {
        toast && toast("Simulate refused", "warn");
        return;
      }
      if (out.claimable_outbox) {
        toast && toast("Simulate must not claim Live outbox", "bad");
        return;
      }
      toast && toast("Simulate OK · no Live outbox", "go");
    } catch {
      toast && toast("Simulate failed", "bad");
    }
  };

  const activate = async () => {
    if (!mutateOk) {
      toast && toast("Activate sealed", "warn");
      return;
    }
    if (!pick) return toast && toast("Select a domain automation", "warn");
    if (!engineArmed) {
      toast && toast("Engine off — activate sealed", "warn");
      return;
    }
    try {
      const out = await run(
        safeDomainPath("post__automations_domain_p_activate", { pathParams: [pick] }),
        { method: "POST", body: {} },
      );
      if (!out || out.ok === false) {
        toast && toast("Activate refused", "warn");
        return;
      }
      const st = String((out.activation && out.activation.status) || out.status || "");
      if (st === "DEGRADED" || st === "NEEDS_REVALIDATION" || st === "HELD") {
        toast && toast("Activation " + st + " — never paints Enabled", "warn");
      } else {
        toast && toast("Activated · " + (st || "ok"), "go");
      }
      lib.reload && lib.reload();
    } catch {
      toast && toast("Activate failed", "bad");
    }
  };

  const hold = async () => {
    if (!mutateOk) {
      toast && toast("Hold sealed", "warn");
      return;
    }
    if (!actId) return toast && toast("Activate first — no activation to hold", "warn");
    try {
      const out = await run(
        safeDomainPath("post__automations_domain_activations_p_hold", { pathParams: [actId] }),
        { method: "POST", body: {} },
      );
      if (!out || out.ok === false) {
        toast && toast("Hold refused", "warn");
        return;
      }
      toast && toast("Held — Trigger denied until Release", "warn");
      lib.reload && lib.reload();
    } catch {
      toast && toast("Hold failed", "bad");
    }
  };

  const release = async () => {
    if (!mutateOk) {
      toast && toast("Release sealed", "warn");
      return;
    }
    if (!actId) return toast && toast("No activation to release", "warn");
    try {
      const out = await run(
        safeDomainPath("post__automations_domain_activations_p_release", { pathParams: [actId] }),
        { method: "POST", body: {} },
      );
      if (!out || out.ok === false) {
        toast && toast("Release refused", "warn");
        return;
      }
      const st = String((out.activation && out.activation.status) || "");
      toast && toast("Released · " + (st || "revalidated"), st === "ENABLED" ? "go" : "warn");
      lib.reload && lib.reload();
    } catch {
      toast && toast("Release failed", "bad");
    }
  };

  const trigger = async () => {
    if (!triggerOk) {
      toast && toast("Trigger sealed", "warn");
      return;
    }
    if (!pick || !actId) return toast && toast("Activate first — Live trigger needs activation", "warn");
    if (!engineArmed) {
      toast && toast("Engine off — trigger sealed", "warn");
      return;
    }
    if (!liveEligible) {
      toast && toast("Not Live eligible — DEGRADED/HELD never Trigger", "warn");
      return;
    }
    try {
      const out = await run(
        safeDomainPath("post__automations_domain_p_trigger", { pathParams: [pick] }),
        { method: "POST", body: { activation_id: actId } },
      );
      if (!out || out.ok === false) {
        toast && toast("Trigger refused · " + String((out && out.reason) || "denied"), "warn");
        return;
      }
      const rid = String((out.run && out.run.run_id) || "");
      setLastRunId(rid);
      toast && toast("Triggered · " + (rid || "run"), "go");
      lib.reload && lib.reload();
    } catch {
      toast && toast("Trigger failed", "bad");
    }
  };

  const cancel = async () => {
    if (!mutateOk) {
      toast && toast("Cancel sealed", "warn");
      return;
    }
    if (!lastRunId) return toast && toast("No run to cancel", "warn");
    try {
      const out = await run(
        safeDomainPath("post__automations_domain_runs_p_cancel", { pathParams: [lastRunId] }),
        { method: "POST", body: { past_commit: false } },
      );
      if (!out || out.ok === false) {
        toast && toast("Cancel refused", "warn");
        return;
      }
      toast && toast("Cancel · " + String(out.cancel_outcome || "ok"), out.may_paint_cancelled ? "go" : "warn");
    } catch {
      toast && toast("Cancel failed", "bad");
    }
  };

  return html`<div className="auto-domain-panel" data-cmc-auto-domain="1" data-cmc-auto-s8="1" data-soft-invent="0">
    <${Card} title="Domain library" meta="define · publish · simulate · activate · hold · trigger · cancel"
      sub="Production domain SoT — Soft invent Enabled forbidden. DEGRADED / HELD / NEEDS_REVALIDATION never paint Live.">
      <${Alert} tone="info" title="Tool plane · not second brain"
        say="Policy/Gateway veto execution. Simulate never claims Live outbox. Admin seat required. Executor must be from the registry."/>
      <${KV} k="Library" v=${lib.loading ? "…" : String(items.length)}/>
      <${KV} k="Executors" v=${exec.loading ? "…" : String(executors.length)}/>
      <${KV} k="Selected activation" v=${actId ? (actSt + (liveEligible ? " · Live eligible" : " · not Enabled")) : "none"}/>
      ${envelope
        ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>`
        : null}
      <div className="rows" style=${{ marginTop: 8 }}>
        ${!items.length
          ? html`<p className="note">No domain automations yet.</p>`
          : items.map((it) => {
              const id = String(it.automation_id || it.id || "");
              const st = String(it.activation_status || it.status || "draft");
              const livePaint = neverEnabledPaint(it);
              return html`<button key=${id} type="button" className="row"
                data-state=${pick === id ? "on" : "off"}
                data-activation-status=${st}
                data-painted-enabled=${livePaint ? "1" : "0"}
                onClick=${() => setPick(id)}>
                <span className="main">
                  <b>${it.name || id}</b>
                  <span>rev ${it.latest_revision || it.revision || 0} · ${st}
                    ${livePaint ? " · Enabled" : " · not Live paint"}</span>
                </span>
              </button>`;
            })}
      </div>
      <${Field} label="New reusable automation">
        <${Input} value=${name} onChange=${(e) => setName(e.target.value)}
          placeholder="Night site notify"/>
      <//>
      <${Field} label="Executor (registry SoT)">
        <select className="input" value=${defaultExecutor}
          onChange=${(e) => setExecutorId(e.target.value)}
          data-cmc-auto-executor="1">
          ${!executors.length
            ? html`<option value="">No executors</option>`
            : executors.map((e) => {
                const id = String(e.executor_id || e.id || "");
                return html`<option key=${id} value=${id}>${e.label || id}</option>`;
              })}
        </select>
      <//>
      <div className="auto-desk__lanes" data-cmc-auto-lanes="1">
        <div className="auto-desk__lane">
          <span className="auto-desk__lane-k">Write</span>
          <div className="btn-row">
            <button type="button" className="btn btn--go btn--sm"
              data-cmc-auto-define-ok=${defineOk ? "1" : "0"}
              disabled=${busy || !defineOk}
              onClick=${defineLib}>Define</button>
            <button type="button" className="btn btn--ghost btn--sm"
              data-cmc-auto-publish-ok=${mutateOk ? "1" : "0"}
              disabled=${busy || !pick || !mutateOk}
              onClick=${publish}>Publish</button>
          </div>
        </div>
        <div className="auto-desk__lane">
          <span className="auto-desk__lane-k">Practice</span>
          <div className="btn-row">
            <button type="button" className="btn btn--ghost btn--sm" disabled=${busy || !pick}
              onClick=${simulate}>Simulate</button>
          </div>
        </div>
        <div className="auto-desk__lane">
          <span className="auto-desk__lane-k">Live</span>
          <div className="btn-row">
            <button type="button" className="btn btn--ghost btn--sm"
              data-cmc-auto-activate-ok=${mutateOk ? "1" : "0"}
              disabled=${busy || !pick || !engineArmed || !mutateOk}
              onClick=${activate}>Activate</button>
            <button type="button" className="btn btn--go btn--sm" data-cmc-auto-trigger="1"
              data-cmc-auto-trigger-ok=${triggerOk ? "1" : "0"}
              disabled=${busy || !actId || !liveEligible || !engineArmed || !triggerOk}
              onClick=${trigger}>Trigger</button>
          </div>
        </div>
        <div className="auto-desk__lane">
          <span className="auto-desk__lane-k">Hold</span>
          <div className="btn-row">
            <button type="button" className="btn btn--ghost btn--sm" data-cmc-auto-hold="1"
              data-cmc-auto-hold-ok=${mutateOk ? "1" : "0"}
              disabled=${busy || !actId || !mutateOk}
              onClick=${hold}>Hold</button>
            <button type="button" className="btn btn--ghost btn--sm" data-cmc-auto-release="1"
              data-cmc-auto-release-ok=${mutateOk ? "1" : "0"}
              disabled=${busy || !actId || actSt !== "HELD" || !mutateOk}
              onClick=${release}>Release</button>
            <button type="button" className="btn btn--ghost btn--sm" data-cmc-auto-cancel="1"
              data-cmc-auto-cancel-ok=${mutateOk ? "1" : "0"}
              disabled=${busy || !lastRunId || !mutateOk}
              onClick=${cancel}>Cancel</button>
          </div>
        </div>
      </div>
      ${lastSim ? html`<p className="note" style=${{ marginTop: 8 }}
          data-cmc-claimable-outbox=${lastSim.claimable_outbox ? "1" : "0"}
          data-cmc-soft-invent=${lastSim.soft_invent ? "1" : "0"}>
        Last simulate · ${lastSim.claimable_outbox ? "queued for send" : "practice only"}
      </p>` : null}
      ${lastRunId ? html`<p className="note">Last run is on file</p>` : null}
    <//>
  </div>`;
}

export default AutomationsDomainPanel;
